import { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import { createCompany } from "../../api/companies";
import { SCompanyCreate, SCompanyOut } from "../../types/companies";
import { BedDouble, Building2, Link2, UsersRound } from "lucide-react";
import { STANDARD_RU_CITIES } from "../../constants/cities";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (newCompany: SCompanyOut) => void;
};

export default function AddCompanyModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [inn, setInn] = useState("");
  const [city, setCity] = useState("");
  const [competitorsCount, setCompetitorsCount] = useState("");
  const [objectsLimit, setObjectsLimit] = useState("1");
  const [otaCount, setOtaCount] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const cityListId = "company-city-suggestions";

  useEffect(() => {
    if (!open) {
      setName("");
      setInn("");
      setCity("");
      setCompetitorsCount("");
      setObjectsLimit("1");
      setOtaCount("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!name.trim()) return setError("Введите название компании");
    if (name.trim().length > 150) return setError("Название слишком длинное (макс. 150 символов)");
    if (inn && !/^[0-9]+$/.test(inn)) return setError("ИНН может содержать только цифры");
    if (competitorsCount.trim() && !/^\d+$/.test(competitorsCount.trim())) return setError("Укажите целое число конкурентов");
    if (!/^\d+$/.test(objectsLimit.trim())) return setError("Укажите целое число для лимита объектов");
    if (otaCount.trim() && !/^\d+$/.test(otaCount.trim())) return setError("Укажите целое число для количества ОТА");
    if (Number(objectsLimit) < 1) return setError("Лимит объектов должен быть не меньше 1");
    if (otaCount.trim() && Number(otaCount) < 1) return setError("Количество ОТА должно быть не меньше 1");

    setSubmitting(true);
    try {
      const payload: SCompanyCreate = {
        company_name: name.trim(),
        inn: inn.trim() || null,
        city: city.trim() || null,
        objects_limit: Number(objectsLimit),
      };
      if (competitorsCount.trim()) {
        payload.competitors_count = Number(competitorsCount);
      }
      if (otaCount.trim()) {
        payload.ota_count = Number(otaCount);
      }
      const created = await createCompany(payload);
      onCreated(created);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Не удалось добавить компанию");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60";

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="text-center space-y-3">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600 shadow-inner">
            <Building2 size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Добавить компанию</h3>
            <p className="mt-1 text-sm text-slate-500">Создайте новую компанию, чтобы привязать к ней объекты и пользователей</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Название компании *</span>
            <input
              className={inputClass}
              placeholder="Например, ООО Ромашка"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">ИНН (необязательно)</span>
            <input
              className={inputClass}
              placeholder="Только цифры"
              value={inn}
              onChange={(e) => setInn(e.target.value)}
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Город (необязательно)</span>
            <input
              className={inputClass}
              placeholder="Например, Москва"
              value={city}
              list={cityListId}
              onChange={(e) => setCity(e.target.value)}
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Кол-во конкурентов на объект размещения</span>
            <div className="relative">
              <UsersRound size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="number"
                min={0}
                step={1}
                className={`${inputClass} pl-11`}
                placeholder="5"
                value={competitorsCount}
                onChange={(e) => setCompetitorsCount(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">Оставьте пустым, чтобы использовать значение по умолчанию с сервера</p>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Макс. количество объектов размещения</span>
            <div className="relative">
              <BedDouble size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="number"
                min={1}
                step={1}
                className={`${inputClass} pl-11`}
                placeholder="1"
                value={objectsLimit}
                onChange={(e) => setObjectsLimit(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">По умолчанию для нового клиента используется лимит 1 объект</p>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Кол-во ОТА по умолчанию</span>
            <div className="relative">
              <Link2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="number"
                min={1}
                step={1}
                className={`${inputClass} pl-11`}
                placeholder="3"
                value={otaCount}
                onChange={(e) => setOtaCount(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">Оставьте пустым, чтобы использовать значение по умолчанию с сервера</p>
          </label>
          <datalist id={cityListId}>
            {STANDARD_RU_CITIES.map((cityOption) => (
              <option key={cityOption} value={cityOption} />
            ))}
          </datalist>
        </div>

        {error && <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-800">{error}</div>}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.3)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Добавляем…" : "Добавить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
